/**
 * Schémas Zod pour le module Annonces (job offers).
 *
 * Le même schéma de base est utilisé côté admin/responsable et côté entreprise,
 * avec des variantes (création orpheline / création rattachée à mon entreprise).
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
export const JOB_CONTRACT_TYPES = ['stage', 'alternance', 'cdd', 'cdi', 'autre'];
export const JOB_OFFER_STATUSES = ['draft', 'published', 'closed'];
export const jobContractTypeSchema = z.enum(JOB_CONTRACT_TYPES);
export const jobOfferStatusSchema = z.enum(JOB_OFFER_STATUSES);
export const JOB_CONTRACT_LABELS = {
    stage: 'Stage',
    alternance: 'Alternance',
    cdd: 'CDD',
    cdi: 'CDI',
    autre: 'Autre',
};
export const JOB_OFFER_STATUS_LABELS = {
    draft: 'Brouillon',
    published: 'Publiée',
    closed: 'Fermée',
};
/**
 * Champs métiers d'une annonce. Sert de base à la création et l'édition.
 *
 * Modèle pivot :
 *   - **formationId obligatoire** : chaque candidat est rattaché à une
 *     formation et une seule (via sa promotion), donc chaque annonce vise
 *     également une formation et une seule.
 *
 * Note candidature : au moins un des trois champs (URL, email, instructions
 * texte) doit être renseigné, sinon le candidat ne sait pas où postuler.
 */
const baseJobOfferFields = {
    title: z.string().trim().min(3, 'Titre requis (3 caractères minimum).').max(150),
    description: z.string().trim().min(20, 'Description requise (20 caractères minimum).').max(5000),
    formationId: uuidSchema,
    sectorId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    location: z.string().trim().max(120).optional().or(z.literal('')),
    contractType: jobContractTypeSchema,
    duration: z.string().trim().max(60).optional().or(z.literal('')),
    startDate: z.coerce.date().optional().nullable(),
    salaryRange: z.string().trim().max(80).optional().or(z.literal('')),
    applicationUrl: z
        .string()
        .trim()
        .max(500)
        .url('URL invalide.')
        .optional()
        .or(z.literal('')),
    applicationEmail: z.string().trim().email('Email invalide.').optional().or(z.literal('')),
    applicationInstructions: z.string().trim().max(2000).optional().or(z.literal('')),
    expiresAt: z.coerce.date().optional().nullable(),
    status: jobOfferStatusSchema.optional(),
    /**
     * Permet (ou non) aux candidats de demander une immersion avant de
     * candidater. Activé par défaut — l'entreprise peut le désactiver pour
     * les annonces qui ne s'y prêtent pas (urgence, poste à distance, etc.).
     */
    immersionEnabled: z.boolean().optional(),
    /**
     * Mode proxy candidature — admin only. Si true, les canaux directs
     * (URL/email) sont masqués côté candidat qui passe par un formulaire
     * sur Le Comptoir. Le Comptoir forward au contact entreprise et garde
     * un historique. Voir migration 20260520214602 + JobApplication.
     */
    proxyApplicationEnabled: z.boolean().optional(),
    /**
     * V7 (mai 2026) — Visibilité fine de l'annonce.
     *   - public                  : ouverte à tous les candidats
     *   - restricted_formations   : visible aux candidats des formations listées
     *   - restricted_establishment: visible aux candidats d'un établissement
     * Default côté serveur = dépend du créateur (admin/responsable →
     * restricted_establishment, company self → public, voir route POST).
     */
    visibility: z
        .enum(['public', 'restricted_formations', 'restricted_establishment'])
        .optional(),
    allowedFormationIds: z.array(z.string().uuid()).optional().default([]),
    restrictedToEstablishmentId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
};
/**
 * Soumission d'une candidature via le mode proxy (étudiant connecté).
 * Le CV utilisé est le plus récent du candidat — récupéré côté serveur,
 * pas envoyé dans le body.
 */
export const submitJobApplicationSchema = z.object({
    message: z.string().trim().max(2000).optional().or(z.literal('')),
});
/**
 * Champs de traçabilité du contact entreprise — admin/responsable seulement.
 *
 * Ces champs sont stockés sur les annonces orphelines (= sans companyId)
 * pour permettre à l'admin de :
 *   - retrouver qui a fourni l'annonce ;
 *   - relancer le contact pour qu'il crée un compte ;
 *   - lui envoyer le magic link de claim de l'annonce.
 *
 * Ils sont **jamais** exposés aux candidats côté API.
 */
const sourceContactFields = {
    sourceContactName: z.string().trim().max(120).optional().or(z.literal('')),
    sourceContactEmail: z.string().trim().email('Email contact invalide.').max(200).optional().or(z.literal('')),
    sourceContactPhone: z.string().trim().max(40).optional().or(z.literal('')),
    sourceContactNotes: z.string().trim().max(2000).optional().or(z.literal('')),
};
/**
 * Création d'une annonce par un admin / responsable.
 * Le nom d'entreprise est saisi librement ; companyId optionnel.
 *
 * V2 (mai 2026) — Latitude maximum côté admin : on N'IMPOSE PAS au moins
 * un canal de candidature. L'admin peut créer une annonce vite faite
 * (titre + description + formation) et compléter les canaux plus tard
 * quand il aura les infos. Le mode proxy (proxyApplicationEnabled) reste
 * aussi un canal valide.
 */
export const adminJobOfferCreateSchema = z.object({
    ...baseJobOfferFields,
    ...sourceContactFields,
    companyName: z.string().trim().min(2, 'Nom de l’entreprise requis.').max(200),
    companyId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
});
/**
 * Édition d'une annonce par un admin / responsable.
 * Tous les champs optionnels — patch partiel. La formation reste
 * modifiable par un admin (au cas où l'annonce a été mal classée).
 */
export const adminJobOfferUpdateSchema = z
    .object({
    title: baseJobOfferFields.title.optional(),
    description: baseJobOfferFields.description.optional(),
    companyName: z.string().trim().min(2).max(200).optional(),
    companyId: uuidSchema.nullable().optional(),
    sectorId: uuidSchema.nullable().optional(),
    formationId: uuidSchema.optional(),
    location: z.string().trim().max(120).optional().or(z.literal('')),
    contractType: jobContractTypeSchema.optional(),
    duration: z.string().trim().max(60).optional().or(z.literal('')),
    startDate: z.coerce.date().optional().nullable(),
    salaryRange: z.string().trim().max(80).optional().or(z.literal('')),
    applicationUrl: z.string().trim().max(500).url().optional().or(z.literal('')),
    applicationEmail: z.string().trim().email().optional().or(z.literal('')),
    applicationInstructions: z.string().trim().max(2000).optional().or(z.literal('')),
    expiresAt: z.coerce.date().optional().nullable(),
    status: jobOfferStatusSchema.optional(),
    immersionEnabled: z.boolean().optional(),
    proxyApplicationEnabled: z.boolean().optional(),
    // V7 (mai 2026) — Visibilité fine. Manquaient dans l'update schema → strippés
    // silencieusement par le middleware validate(), jamais persistés. Fix #bug (PR #22).
    visibility: baseJobOfferFields.visibility,
    // NB : PAS de .default([]) ici (contrairement à baseJobOfferFields.allowedFormationIds).
    // Avec default([]), un PUT partiel (ex: title seul) produce allowedFormationIds:[]
    // dans le body parsé → emptyToUndefined passe [] → Prisma écrase la liste en BDD.
    // Sans default, le champ absent reste undefined → Prisma ne touche pas la colonne.
    allowedFormationIds: z.array(z.string().uuid()).optional(),
    restrictedToEstablishmentId: baseJobOfferFields.restrictedToEstablishmentId,
    ...sourceContactFields,
});
/**
 * Création d'une annonce par une entreprise.
 * companyName est ignoré — toujours = à la raison sociale du compte.
 * companyId est forcé serveur-side au userId du compte.
 */
export const companyJobOfferCreateSchema = z
    .object(baseJobOfferFields)
    .superRefine(applicationChannelRequired);
/**
 * Édition d'une annonce par une entreprise.
 */
export const companyJobOfferUpdateSchema = z.object({
    title: baseJobOfferFields.title.optional(),
    description: baseJobOfferFields.description.optional(),
    formationId: uuidSchema.optional(),
    sectorId: uuidSchema.nullable().optional(),
    location: z.string().trim().max(120).optional().or(z.literal('')),
    contractType: jobContractTypeSchema.optional(),
    duration: z.string().trim().max(60).optional().or(z.literal('')),
    startDate: z.coerce.date().optional().nullable(),
    salaryRange: z.string().trim().max(80).optional().or(z.literal('')),
    applicationUrl: z.string().trim().max(500).url().optional().or(z.literal('')),
    applicationEmail: z.string().trim().email().optional().or(z.literal('')),
    applicationInstructions: z.string().trim().max(2000).optional().or(z.literal('')),
    expiresAt: z.coerce.date().optional().nullable(),
    status: jobOfferStatusSchema.optional(),
    immersionEnabled: z.boolean().optional(),
});
/**
 * Rattachement d'une annonce orpheline à une entreprise (admin-side).
 */
export const claimJobOfferSchema = z.object({
    companyId: uuidSchema,
});
/**
 * Demande d'envoi d'un magic-link de claim au contact source.
 * Email optionnel — si vide, on prend celui stocké sur l'annonce.
 */
export const sendClaimEmailSchema = z.object({
    email: z.string().trim().email('Email invalide.').max(200).optional(),
    stage: z.enum(['j0', 'j7', 'j30', 'manual']).default('manual'),
});
/**
 * Token claim côté public : 32 octets en base64url ≈ 43 chars.
 * On accepte 32 à 64 caractères url-safe par tolérance.
 */
export const claimTokenSchema = z
    .string()
    .min(32)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'Token invalide.');
/**
 * Body de claim public — vide pour l'instant, mais on l'expose pour
 * pouvoir ajouter `companyId` (si l'utilisateur a plusieurs comptes
 * company, peu probable mais ouvert) sans casser l'API.
 */
export const claimByTokenSchema = z.object({});
/**
 * Filtres de recherche côté candidat. Le filtrage par formation est
 * automatique (serveur-side, basé sur la formation du candidat connecté).
 */
export const studentJobOfferFiltersSchema = z.object({
    q: z.string().trim().max(100).optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    sectorId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    contractType: jobContractTypeSchema.optional(),
    location: z.string().trim().max(60).optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    // V2 (mai 2026) — filtre formation OPTIONNEL côté étudiant. Permet à
    // l'étudiant de voir toutes les annonces (cross-formation) ou de
    // restreindre à une formation précise. Si non fourni : toutes les
    // formations sont visibles (sa propre formation remonte en tête via le tri).
    formationId: uuidSchema.optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    // Filtre "uniquement ma formation" — pratique pour la dropdown UI :
    // si true, on filtre serveur-side sur la formation du candidat.
    myFormationOnly: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
/**
 * Filtres de recherche côté admin / responsable.
 */
/**
 * Helper : accepte un UUID, undefined ou chaîne vide (cas fréquent quand
 * le frontend envoie `?companyId=` sans valeur). Renvoie undefined dans
 * ces 2 derniers cas pour ne pas filtrer côté Prisma.
 */
const optionalUuid = uuidSchema
    .optional()
    .or(z.literal(''))
    .transform((v) => (v ? v : undefined));
export const adminJobOfferFiltersSchema = z.object({
    q: z.string().trim().max(100).optional().or(z.literal('')).transform((v) => (v ? v : undefined)),
    status: jobOfferStatusSchema.optional(),
    orphanOnly: z.coerce.boolean().optional(),
    /** Filtre les annonces ayant une localisation mais sans coordonnées GPS (non géolocalisées). */
    geoMissing: z.coerce.boolean().optional(),
    companyId: optionalUuid,
    formationId: optionalUuid,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ─── Helpers ─────────────────────────────────────────────────────────────
/**
 * Au moins un canal de candidature doit être renseigné, **sauf** si
 * le mode proxy est activé (le formulaire interne Le Comptoir prend
 * alors la place du canal externe). S'applique uniquement aux schémas
 * entreprise (les admin/responsable ont latitude maximum — cf. note
 * sur `adminJobOfferCreateSchema`).
 */
function applicationChannelRequired(data, ctx) {
    if (data.proxyApplicationEnabled)
        return; // le proxy fait le canal
    if (!data.applicationUrl &&
        !data.applicationEmail &&
        !data.applicationInstructions) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Indiquez au moins un canal de candidature (URL, email ou instructions).",
            path: ['applicationInstructions'],
        });
    }
}
// ─────────────────────────────────────────────────────────────────────────
// CRM Recrutement V13 — suivi des processus de sélection
// ─────────────────────────────────────────────────────────────────────────
export const RECRUITMENT_STATUSES = [
    'new_application',
    'under_review',
    'shortlisted',
    'interview_scheduled',
    'offer_made',
    'hired',
    'rejected',
    'withdrawn',
];
export const RECRUITMENT_STATUS_LABELS = {
    new_application: 'Envoyée',
    under_review: 'En cours d\'étude',
    shortlisted: 'Présélectionné·e',
    interview_scheduled: 'Entretien prévu',
    offer_made: 'Offre reçue',
    hired: 'Embauché·e',
    rejected: 'Refusée',
    withdrawn: 'Retirée',
};
/** Couleur sémantique pour les badges (clé CSS Tailwind). */
export const RECRUITMENT_STATUS_COLOR = {
    new_application: 'passage-outre',
    under_review: 'passage-tomate',
    shortlisted: 'passage-menthe',
    interview_scheduled: 'passage-beurre',
    offer_made: 'passage-menthe',
    hired: 'passage-menthe',
    rejected: 'destructive',
    withdrawn: 'passage-ink-soft',
};
export const APPLICATION_CHANNELS = [
    'platform',
    'email',
    'phone',
    'linkedin',
    'in_person',
    'other',
];
export const APPLICATION_CHANNEL_LABELS = {
    platform: 'Via Le Comptoir',
    email: 'Email direct',
    phone: 'Téléphone',
    linkedin: 'LinkedIn',
    in_person: 'Candidature spontanée',
    other: 'Autre',
};
export const INTERACTION_TYPES = [
    'email_sent',
    'email_received',
    'phone_outbound',
    'phone_inbound',
    'meeting',
    'document_sent',
    'document_received',
    'note',
    'status_update',
];
export const INTERACTION_TYPE_LABELS = {
    email_sent: 'Email envoyé',
    email_received: 'Email reçu',
    phone_outbound: 'Appel passé',
    phone_inbound: 'Appel reçu',
    meeting: 'Rencontre / entretien',
    document_sent: 'Document envoyé',
    document_received: 'Document reçu',
    note: 'Note interne',
    status_update: 'Mise à jour du statut',
};
export const DOCUMENT_TYPES = [
    'cv',
    'cover_letter',
    'portfolio',
    'references',
    'diplomes',
    'other',
];
export const DOCUMENT_TYPE_LABELS = {
    cv: 'CV',
    cover_letter: 'Lettre de motivation',
    portfolio: 'Portfolio',
    references: 'Références',
    diplomes: 'Diplômes / relevés de notes',
    other: 'Autre document',
};
export const recruitmentStatusSchema = z.enum(RECRUITMENT_STATUSES);
export const applicationChannelSchema = z.enum(APPLICATION_CHANNELS);
export const interactionTypeSchema = z.enum(INTERACTION_TYPES);
/** PATCH /etudiant/job-applications/:id/tracking — mise à jour côté candidat */
export const updateStudentTrackingSchema = z.object({
    applicationChannel: applicationChannelSchema.optional(),
    documentsSent: z.array(z.string().max(50)).max(10).optional(),
    studentNextAction: z.string().trim().max(300).optional().nullable(),
    studentNextActionAt: z.coerce.date().optional().nullable(),
    studentNotes: z.string().trim().max(2000).optional().nullable(),
});
/** PATCH /company/job-applications/:id/tracking — mise à jour côté entreprise */
export const updateCompanyTrackingSchema = z.object({
    recruitmentStatus: recruitmentStatusSchema.optional(),
    companyNextAction: z.string().trim().max(300).optional().nullable(),
    companyNextActionAt: z.coerce.date().optional().nullable(),
    companyNotes: z.string().trim().max(2000).optional().nullable(),
});
/** POST /etudiant|company/job-applications/:id/interactions — journal */
export const addInteractionSchema = z.object({
    type: interactionTypeSchema,
    description: z.string().trim().max(1000).optional().nullable(),
    occurredAt: z.coerce.date().default(() => new Date()),
});
/** Filtres candidatures entreprise — vue globale multi-annonces */
export const companyApplicationFiltersSchema = z.object({
    q: z.string().trim().max(100).optional(),
    recruitmentStatus: recruitmentStatusSchema.optional(),
    overdueOnly: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
//# sourceMappingURL=jobOffer.js.map